// HTML export (TODO §6.6 "the same snapshots unlock an HTML export"). Produces a SINGLE,
// self-contained `.html` file that plays the deck as a PRESENTATION — one slide at a time,
// keyboard + on-screen navigation, and the deck's slide transitions — with no server and no
// runtime dependency:
//
//   • Gridd grids  → the pre-captured snapshot PNGs (data: URLs), the same images the PDF
//                    and presentation paths use (§6.6.4). No live iframe, no @gridd/embed at
//                    view time — the file is fully offline.
//   • images       → baked to data: URLs (crop/rotation/flip/filters flattened onto a canvas
//                    at export time), so nothing is fetched when the file is opened.
//   • charts       → the real vector SVG, serialized from an off-screen render (§15/§9.2).
//                    A chart is same-origin React drawing an SVG, so unlike a grid or a gantt
//                    there is nothing to rasterize AROUND — the file gets the actual shapes,
//                    a few KB each and crisp at any zoom. The reasoning, including why a
//                    gantt does not get the same treatment, is on `collectChartSvgs`.
//   • gantts       → a 2x PNG baked off-screen from the store's schedule (§26/§9.2): the
//                    widget is React + 150KB of vendor CSS, so the single offline file gets
//                    a raster, exactly as a grid does.
//   • diagrams     → the same vector SVG the editor draws at rest, inlined, with the FONTS
//                    inlined too (§21/§5.4) — in-app renders skip them because the fonts are
//                    already loaded; this file has to open on a machine that has never seen
//                    the deck. Its images need nothing extra IF they went to the `assets`
//                    store as ordinary blobs (§4.4.2); if they did not, this is where it
//                    surfaces, as images that vanish only in the export.
//   • text         → serialized from its ProseMirror doc to static HTML.
//   • slide chrome → title/subtitle/sticker/source/page-number reproduced as inline CSS
//                    against the same 1600×900 logical canvas the editor authors on, so an
//                    exported slide matches what you edited.
//   • navigation   → a small inlined vanilla-JS player mirrors `PresentationView` (§5/§10):
//                    a letterboxed 16:9 stage, arrow/space/PageUp-Down keys, click-to-advance,
//                    Home/End, an `f` fullscreen toggle, and the SAME slide-enter/slide-leave
//                    keyframes driven by inline CSS custom properties, so fade/slide/push/
//                    zoom/flip all replay exactly as in the app.
//
// `buildDeckHtml` is a pure string builder (given already-resolved snapshot/image maps);
// `collectImageDataUrls` is the browser-side step that reads the asset blobs and bakes them.

import type { GriddSnapshot } from "@gridd/embed/react";

import { logoBox, logoOffsets, logoReserve } from "@/lib/deck-logo";
import {
  cssFilter,
  effectiveFit,
  imageBackground,
  type ImageFit,
} from "@/lib/image";
import { getAsset } from "@/lib/idb";
import { slideNumbering } from "@/lib/slide-numbering";
import { escapeHtml, proseMirrorToHtml } from "@/lib/proseMirrorToHtml";
import { saveFile } from "@/lib/saveFile";
import { SLIDE_H, SLIDE_W } from "@/lib/slide-size";
import { clampDuration, resolveEasing } from "@/lib/slide-transition";
import {
  ptToPx,
  resolveRoleTypography,
  resolveStickerTone,
  resolveStudioTypography,
  type ResolvedTypography,
} from "@/lib/stylesheet-typography";
import { renderDiagramSvgForExport } from "@/lib/diagramSvg";
import type { DiagramDocument } from "@/types/diagram";
import type { ImageContent, SlideContent } from "@/types/content";
import type {
  DeckLogo,
  GriddStyleSheetData,
  SlideData,
  SlideTransition,
} from "@/types/document";

// A baked image asset: its self-contained data URL and how it fills its window. The fit is
// NOT decided here — it comes from `effectiveFit`, the same helper ImageBlock renders with, so
// the exported file cannot crop an image differently from the editor (B4 fix, part 2). Under
// `contain` the `object-fit` below cannot crop the baked bitmap at all, which is what makes
// the two paths agree by construction rather than by matching arithmetic.
export interface BakedImage {
  dataUrl: string;
  fit: ImageFit;
}

// §24.4.1 The deck's logo, baked. Unlike a slide image it has no crop/transform/filter
// pipeline — there is nothing to flatten — so the bake is just "blob → data URL", and SVG
// and raster take the same path. Geometry is NOT carried here: both renderers read it from
// `deck-logo.ts` off the same `corner`/`sizePct`, which is what keeps them from drifting
// (§24.4.2).
export interface BakedLogo {
  dataUrl: string;
  corner: DeckLogo["corner"];
  sizePct: number;
}

// Escape a value going into a double-quoted HTML attribute (inline styles, alt text).
function attr(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
}

// A resolved typography role → a CSS declaration string. Sizes are authored in points and
// converted to px here (the logical canvas is a 96dpi surface, §1.4), matching Slide.tsx.
function typographyCss(t: ResolvedTypography): string {
  const parts = [`font-size:${ptToPx(t.fontSizePt)}px`, `color:${t.color}`];
  if (t.fontFamily) parts.push(`font-family:${t.fontFamily}`);
  if (t.fontWeight) parts.push(`font-weight:${t.fontWeight}`);
  if (t.fontStyle) parts.push(`font-style:${t.fontStyle}`);
  return parts.join(";");
}

// One content window → its inner HTML. Mirrors ContentWindow's render branches, but static.
function renderWindow(
  content: SlideContent,
  styleSheet: GriddStyleSheetData,
  snapshots: Record<string, GriddSnapshot>,
  images: Record<string, BakedImage>,
  diagrams: Record<string, string>,
  gantts: Record<string, string>,
  charts: Record<string, string>,
  grow: number | null,
): string {
  const growStyle = grow == null ? "" : `flex-grow:${grow};`;
  const open = `<div class="win" style="${growStyle}">`;

  if (content?.kind === "gridd") {
    const snap = snapshots[content.griddDocId];
    const inner = snap
      ? `<img class="grid-img" src="${attr(snap.dataUrl)}" alt="">`
      : "";
    return `${open}<div class="frame">${inner}</div></div>`;
  }

  if (content?.kind === "text") {
    // Base typography resolves against the deck stylesheet's Body role, same source as
    // the editor (§2.4); marks in the doc override it.
    const base = typographyCss(resolveRoleTypography(styleSheet, "body"));
    return `${open}<div class="frame text"><div class="text-body" style="${attr(base)}">${proseMirrorToHtml(
      content.json,
    )}</div></div></div>`;
  }

  if (content?.kind === "diagram") {
    // §11.2 The SVG string, inline. No iframe, no snapshot registry, no `requestSnapshots`
    // round trip — a diagram is same-origin React, so the exported file gets the real vector
    // rather than a raster stand-in. `.diagram-frame` fits it (§3.4 letterboxes, never
    // re-crops), which is the same rule the editor's window applies.
    const svg = diagrams[content.diagramDocId];
    return `${open}<div class="frame diagram-frame">${svg ?? ""}</div></div>`;
  }

  if (content?.kind === "gantt") {
    // §26/§9.2 A baked PNG, fitted rather than cropped — the same rule the diagram frame
    // applies, and the reason an approximate authoring box is good enough.
    const png = gantts[content.ganttDocId];
    const inner = png
      ? `<img class="gantt-img" src="${attr(png)}" alt="">`
      : "";
    return `${open}<div class="frame gantt-frame">${inner}</div></div>`;
  }

  if (content?.kind === "chart") {
    // §15/§9.2 The serialized SVG, inline — no `<img>`, no data URL. `.chart-frame` fits it
    // the way `.diagram-frame` fits a diagram: letterbox, never re-crop, which is the §21/§3.4
    // rule this export applies to every embedded drawing.
    //
    // The string is NOT escaped, and that is deliberate: it is markup we generated ourselves
    // one function call ago from our own React tree, exactly like the diagram branch above.
    const svg = charts[content.chartDocId];
    return `${open}<div class="frame chart-frame">${svg ?? ""}</div></div>`;
  }

  if (content?.kind === "image") {
    const baked = images[content.assetId];
    const inner = baked
      ? `<img class="img" style="object-fit:${baked.fit}" src="${attr(baked.dataUrl)}" alt="">`
      : "";
    // §22.2 The window's background, resolved from the content's colour NAME against the
    // deck sheet by the same helper the editor renders through — so the letterbox bars in the
    // exported file are the colour the author picked, not a stylesheet away from it. Inline
    // because it is per window; `.img-frame`'s rule is only the default.
    const bg = imageBackground(styleSheet, content);
    return `${open}<div class="frame img-frame" style="background:${attr(bg)}">${inner}</div></div>`;
  }

  // Empty window — render an empty frame so the layout/spacing is preserved.
  return `${open}<div class="frame empty"></div></div>`;
}

// One active slide → the `.slide`/`.canvas` markup (a 16:9 stage-filling card). `pageNumber`
// is the slide's place in the pyramid — `4`, `4.2`, `B7`, or null on a cover — resolved by
// `slideNumbering` (§18.2.9), never from a position here. The canvas is authored at SLIDE_W×
// SLIDE_H and scaled to the stage via CSS container units, so it never reflows — the same
// invariant as the editor/present/print.
function renderSlideCard(
  slide: SlideData,
  pageNumber: string | null,
  styleSheet: GriddStyleSheetData,
  snapshots: Record<string, GriddSnapshot>,
  images: Record<string, BakedImage>,
  diagrams: Record<string, string>,
  gantts: Record<string, string>,
  charts: Record<string, string>,
  logo: BakedLogo | null,
): string {
  // Title/subtitle/source come from SlideCraft's own presets in the deck sheet, the
  // sticker from its highlight role — the same resolution Slide.tsx runs (§7.4).
  const titleCss = typographyCss(resolveStudioTypography(styleSheet, "Title"));
  const subCss = typographyCss(resolveStudioTypography(styleSheet, "Subtitle"));
  const srcCss = typographyCss(resolveStudioTypography(styleSheet, "Source"));
  // §23.3.3 The same three things as `Slide.tsx`: the sticker's own preset, the tone pair as
  // inline colours (the tone is PER SLIDE, so it cannot live in the shared `.sticker` rule),
  // and uppercase as a display transform.
  const stickerCss = typographyCss(
    resolveStudioTypography(styleSheet, "Sticker"),
  );
  const tone = slide.stickerTone
    ? resolveStickerTone(styleSheet, slide.stickerTone)
    : null;
  const toneCss =
    tone && slide.sticker
      ? `;background:${tone.background};color:${tone.color};border-color:${tone.background}`
      : "";

  // §24.2.3 Every layout carries the mark, the cover included, in the same corner.
  const markHtml = logo
    ? `<img class="logo" src="${attr(logo.dataUrl)}" alt="" aria-hidden="true" style="${attr(
        cssDecls({
          ...logoOffsets(logo.corner),
          ...logoBox(logo.sizePct),
        }),
      )}">`
    : "";
  // §24.2.2 The one padding rule, from the shared helper — never retyped here. Stated by
  // edge; the two layouts below map it to their own rows.
  const reserve = logoReserve(logo ?? undefined);
  const pad = (left: number, right: number) =>
    left || right ? `padding-left:${left}px;padding-right:${right}px` : "";

  // §19 The cover: four corner fields around a vertically centred title block, and no
  // content region. Rendered from the same `layout` the editor branches on, against the
  // same stylesheet presets, so the exported cover is the edited one (§19.2.1).
  if (slide.layout === "cover") {
    const corner = (value: string | undefined, right: boolean) =>
      `<div class="corner${right ? " right" : ""}" style="${attr(srcCss)}">${
        value ? escapeHtml(value) : "&nbsp;"
      }</div>`;
    const c = slide.corners ?? {};
    return `<div class="slide"><div class="canvas cover">${markHtml}
  <div class="crow" style="${attr(pad(reserve.topLeft, reserve.topRight))}">${corner(
    c.tl,
    false,
  )}${corner(c.tr, true)}</div>
  <div class="cmid">
    <div class="ctitle" style="${attr(titleCss)}">${escapeHtml(slide.title)}</div>
    <div class="csub" style="${attr(subCss)}">${
      slide.subtitle ? escapeHtml(slide.subtitle) : "&nbsp;"
    }</div>
  </div>
  <div class="crow bot" style="${attr(
    pad(reserve.bottomLeft, reserve.bottomRight),
  )}">${corner(c.bl, false)}${corner(c.br, true)}</div>
</div></div>`;
  }

  const ratio = slide.splitRatio ?? 0.5;
  const isTwo = slide.layout === "two-h" || slide.layout === "two-v";
  const contentDir = slide.layout === "two-v" ? "column" : "row";

  const windows = isTwo
    ? renderWindow(
        slide.contents[0] ?? null,
        styleSheet,
        snapshots,
        images,
        diagrams,
        gantts,
        charts,
        ratio,
      ) +
      renderWindow(
        slide.contents[1] ?? null,
        styleSheet,
        snapshots,
        images,
        diagrams,
        gantts,
        charts,
        1 - ratio,
      )
    : renderWindow(
        slide.contents[0] ?? null,
        styleSheet,
        snapshots,
        images,
        diagrams,
        gantts,
        charts,
        null,
      );

  // Subtitle/sticker row: ALWAYS emitted, both halves, matching Slide.tsx (B4 fix, part 3).
  // An empty field is made invisible rather than dropped, so the content window below is the
  // same height here as it is in the editor — otherwise the exported file letterboxes and
  // crops images against a box the author never saw. `&nbsp;` holds the line box open. An
  // empty sticker keeps its height that way but is collapsed to zero WIDTH (see .is-empty),
  // so the subtitle wraps at the full width, exactly as it does in the editor at rest.
  const subtitleRow = `<div class="subrow" style="${attr(
    pad(reserve.topLeft, reserve.topRight),
  )}">${`<div class="subtitle" style="${attr(subCss)}">${
    slide.subtitle ? escapeHtml(slide.subtitle) : "&nbsp;"
  }</div>`}${`<span class="sticker${slide.sticker ? "" : " is-empty"}" style="${attr(
    stickerCss + toneCss,
  )}">${slide.sticker ? escapeHtml(slide.sticker) : "&nbsp;"}</span>`}</div>`;

  return `<div class="slide"><div class="canvas">${markHtml}
  <div class="title" style="${attr(
    `${titleCss};${pad(reserve.topLeft, reserve.topRight)}`,
  )}">${escapeHtml(slide.title)}</div>
  ${subtitleRow}
  <div class="content" style="flex-direction:${contentDir}">${windows}</div>
  <div class="srcrow" style="${attr(pad(reserve.bottomLeft, reserve.bottomRight))}">
    <div class="source" style="${attr(srcCss)}">${escapeHtml(slide.source)}</div>
    ${pageNumber === null ? "" : `<span class="pageno">${escapeHtml(pageNumber)}</span>`}
  </div>
</div></div>`;
}

// A style object of numeric px values → a CSS declaration string. Only used for the logo
// box, whose offsets come back from `logoOffsets` as a partial record.
function cssDecls(style: Record<string, number | undefined>): string {
  return Object.entries(style)
    .filter(([, v]) => v !== undefined)
    .map(
      ([k, v]) => `${k.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}:${v}px`,
    )
    .join(";");
}

// The static stylesheet for the exported presentation player. Reproduces the slide's logical-
// canvas layout (padding/gaps from Slide.tsx), the content-window frames from ContentWindow,
// and the presentation stage + transition keyframes from styles.css §10 verbatim (the two
// keyframes are the ONLY motion; the JS sets the inline custom properties they read).
function styleBlock(): string {
  return `
  *{box-sizing:border-box}
  html,body{margin:0;padding:0;height:100%}
  body{background:#000;color:#0f172a;font-family:Arial,system-ui,sans-serif;overflow:hidden}
  .stage-wrap{position:fixed;inset:0;display:flex;align-items:center;justify-content:center;
    background:#000}
  /* Letterbox: 16:9 stage scaled to whichever viewport dimension binds (matches §5.1). */
  .slide-stage{position:relative;overflow:hidden;perspective:1600px;
    width:min(100vw, calc(100vh * 16 / 9));aspect-ratio:16/9}
  .slide-motion{position:absolute;inset:0;display:none;align-items:center;
    justify-content:center;backface-visibility:hidden;
    animation-duration:var(--slide-dur,400ms);
    animation-timing-function:var(--slide-ease,ease-in-out);animation-fill-mode:both}
  .slide-motion.is-visible{display:flex}
  .slide-motion.slide-entering{animation-name:slide-enter;z-index:2}
  .slide-motion.slide-leaving{animation-name:slide-leave;z-index:1}
  @keyframes slide-enter{
    from{opacity:var(--enter-opacity,1);
      transform:translate(var(--enter-x,0px),var(--enter-y,0px))
        scale(var(--enter-scale,1)) rotateY(var(--enter-rotate,0deg))}
    to{opacity:1;transform:translate(0px,0px) scale(1) rotateY(0deg)}}
  @keyframes slide-leave{
    from{opacity:1;transform:translate(0px,0px) scale(1) rotateY(0deg)}
    to{opacity:var(--leave-opacity,1);
      transform:translate(var(--leave-x,0px),var(--leave-y,0px))
        scale(var(--leave-scale,1)) rotateY(var(--leave-rotate,0deg))}}
  @media (prefers-reduced-motion: reduce){
    .slide-motion.slide-entering,.slide-motion.slide-leaving{animation-duration:.01ms!important}}
  /* Container units scale the fixed 1600x900 canvas to the stage width with no per-frame JS. */
  .slide{width:100%;height:100%;container-type:inline-size;position:relative;
    overflow:hidden;background:#fff}
  .canvas{position:absolute;top:0;left:0;width:${SLIDE_W}px;height:${SLIDE_H}px;
    transform:scale(calc(100cqw / ${SLIDE_W}));transform-origin:top left;
    padding:24px;display:flex;flex-direction:column;gap:12px}
  .title{border-bottom:1px dashed #e2e8f0;padding:4px 0;line-height:1.25;white-space:pre-wrap;overflow-wrap:break-word}
  .subrow{display:flex;align-items:flex-start;gap:12px}
  .subtitle{min-width:0;flex:1;line-height:1.25;white-space:pre-wrap;overflow-wrap:break-word}
  /* Matches the editor's sticker <Input>: w-40 (160px), 1px border, py-0.5/px-3, centred —
     so the row is the same height and the subtitle wraps at the same width (B4 part 3). */
  .sticker{flex:none;width:160px;text-align:center;border-radius:9999px;background:#f1f5f9;
    border:1px solid transparent;padding:2px 12px;overflow:hidden;
    text-overflow:ellipsis;white-space:nowrap;text-transform:uppercase}
  /* Empty: no footprint at all (matching Slide.tsx), but still emitted with an &nbsp; so it
     holds the row's line box and the content window keeps the height the author saw. The
     negative margin swallows .subrow's 12px gap. */
  .sticker.is-empty{width:0;padding:2px 0;border:0;margin-left:-12px;background:transparent;
    visibility:hidden}
  .content{display:flex;flex:1;min-height:0;gap:12px}
  .win{display:flex;flex:1;min-width:0;min-height:0}
  .frame{flex:1;min-width:0;min-height:0;overflow:hidden;border-radius:6px;
    border:1px solid #e2e8f0}
  .grid-img{width:100%;height:100%;object-fit:contain}
  .img{width:100%;height:100%}
  /* §22.2 The default only — every image frame carries its own inline background, resolved
     from the slide's stored colour name. */
  .img-frame{background:#fff}
  .empty{background:#f8fafc}
  /* §21/§3.4 The diagram is FITTED into its window, never re-cropped: the frame element
     keeps its authored size and letterboxes when the layout's aspect differs. Same rule as
     DiagramBlock, so the exported file and the editor agree by construction. */
  .diagram-frame{display:flex;align-items:center;justify-content:center;background:#fff}
  .diagram-frame>svg{max-width:100%;max-height:100%;width:auto;height:auto}
  /* §26/§9.2 The gantt's baked PNG, fitted into the window it was authored for. */
  .gantt-frame{display:flex;align-items:center;justify-content:center;background:#fff}
  .gantt-img{max-width:100%;max-height:100%;width:auto;height:auto}
  /* §15/§9.2 The chart's inline SVG, fitted rather than cropped — the same rule the diagram
     frame applies, which is what makes the approximate authoring box good enough. The viewBox
     set during the bake is what lets width:auto actually scale rather than clip. */
  .chart-frame{display:flex;align-items:center;justify-content:center;background:#fff}
  .chart-frame>svg{max-width:100%;max-height:100%;width:auto;height:auto}
  .text{border:1px solid #e2e8f0}
  .text-body{width:100%;height:100%;overflow:hidden;padding:16px;line-height:1.4}
  .text-body p{margin:0 0 .5em}
  .text-body ul,.text-body ol{margin:0 0 .5em;padding-left:1.4em}
  .text-body li p{margin:0}
  /* §19 Cover: two corner rows around a vertically centred title block. Mirrors the
     editor's canvas exactly — gap-6 between the corners, gap-4 in the middle, py-1 on the
     corner fields — so the exported cover is the one that was edited. */
  .crow{display:flex;align-items:flex-start;gap:24px}
  .crow.bot{align-items:flex-end}
  /* The transparent dashed rule is the editor's guide at rest: kept, not dropped, so the
     row is the same height here as it is on screen (see CornerField's comment). */
  .corner{min-width:0;flex:1;padding:4px 0;line-height:1.25;overflow:hidden;
    text-overflow:ellipsis;white-space:nowrap;border-bottom:1px dashed transparent}
  .corner.right{text-align:right}
  .cmid{display:flex;flex:1;min-height:0;flex-direction:column;justify-content:center;gap:16px}
  .ctitle{padding:4px 0;line-height:1.25;white-space:pre-wrap;overflow-wrap:break-word;
    border-bottom:1px dashed transparent}
  .csub{line-height:1.25;white-space:pre-wrap;overflow-wrap:break-word}
  /* §24.2.1 The mark, positioned inside the logical canvas; the inline style carries the
     corner offsets and the box, both from deck-logo.ts. */
  .logo{position:absolute;object-fit:contain;pointer-events:none;user-select:none}
  .srcrow{display:flex;align-items:center;gap:12px;border-top:1px dashed #e2e8f0;padding-top:2px}
  /* Typography is inline, from the sheet's Source preset — only the box is styled here. */
  .source{min-width:0;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .pageno{flex:none;font-size:20px;color:#94a3b8;font-variant-numeric:tabular-nums}
  /* On-screen navigation (fades in on hover/focus so it never sits on the slide). */
  .controls{position:fixed;bottom:16px;left:50%;transform:translateX(-50%);z-index:10;
    display:flex;align-items:center;gap:4px;padding:6px 10px;color:#fff;font-size:14px;
    background:rgba(15,23,42,.72);border-radius:9999px;opacity:0;transition:opacity .2s}
  .stage-wrap:hover .controls,.controls:focus-within{opacity:1}
  .controls button{background:transparent;border:0;color:#fff;cursor:pointer;font-size:18px;
    line-height:1;padding:4px 10px;border-radius:9999px}
  .controls button:hover{background:rgba(255,255,255,.16)}
  .counter{min-width:58px;text-align:center;font-variant-numeric:tabular-nums;padding:0 4px}
`;
}

// The inlined vanilla-JS player. A direct port of PresentationView's state machine (§5/§10):
// keeps every slide layer in the DOM, shows one as current, and on a step animates the
// entering + leaving layers with the same custom properties `motionStyle` sets in the app.
// `transition` is baked (duration clamped, easing resolved) at build time, so the runtime is
// just the direction/type → CSS-var mapping. No external references — local scripting only.
function playerScript(transition: SlideTransition): string {
  const baked = {
    type: transition.type,
    durationMs: clampDuration(transition.durationMs),
    easing: resolveEasing(transition.easing),
    direction: transition.direction ?? "left",
  };
  return `
(function(){
  var T = ${JSON.stringify(baked)};
  var slides = Array.prototype.slice.call(document.querySelectorAll('.slide-motion'));
  var n = slides.length;
  if (!n) return;
  var counter = document.querySelector('.counter');
  var idx = 0, leaveTimer = null;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var animated = T.type !== 'none' && !reduce;
  var DIR = { left:[-1,0], right:[1,0], up:[0,-1], down:[0,1] };
  var VARS = ['--slide-dur','--slide-ease','--enter-opacity','--enter-x','--enter-y',
    '--enter-scale','--enter-rotate','--leave-opacity','--leave-x','--leave-y',
    '--leave-scale','--leave-rotate'];

  function clearVars(el){ VARS.forEach(function(p){ el.style.removeProperty(p); }); }
  function setVars(el, v){ Object.keys(v).forEach(function(k){ el.style.setProperty(k, String(v[k])); }); }
  function hide(el){ el.classList.remove('is-visible','is-current','slide-entering','slide-leaving'); clearVars(el); }

  function motionVars(navDir, role){
    var v = { '--slide-dur': T.durationMs + 'ms', '--slide-ease': T.easing };
    var b = DIR[T.direction] || DIR.left, advX = b[0]*navDir, advY = b[1]*navDir;
    if (role === 'enter') {
      if (T.type === 'fade') v['--enter-opacity'] = 0;
      else if (T.type === 'slide' || T.type === 'push') { v['--enter-x'] = (-advX*100)+'%'; v['--enter-y'] = (-advY*100)+'%'; }
      else if (T.type === 'zoom') { v['--enter-scale'] = 0.85; v['--enter-opacity'] = 0; }
      else if (T.type === 'flip') { v['--enter-rotate'] = (navDir>0?90:-90)+'deg'; v['--enter-opacity'] = 0; }
    } else {
      if (T.type === 'fade') v['--leave-opacity'] = 0;
      else if (T.type === 'push') { v['--leave-x'] = (advX*100)+'%'; v['--leave-y'] = (advY*100)+'%'; }
      else if (T.type === 'zoom') { v['--leave-scale'] = 1.1; v['--leave-opacity'] = 0; }
      else if (T.type === 'flip') { v['--leave-rotate'] = (navDir>0?-90:90)+'deg'; v['--leave-opacity'] = 0; }
      // 'slide' is a cover: outgoing holds still beneath the incoming one, no leave vars.
    }
    return v;
  }

  // Force-complete any in-flight transition: current idx becomes the sole visible layer.
  function finalize(){
    if (leaveTimer){ clearTimeout(leaveTimer); leaveTimer = null; }
    slides.forEach(function(s, i){
      if (i === idx){ clearVars(s); s.classList.remove('slide-entering','slide-leaving'); s.classList.add('is-visible','is-current'); }
      else hide(s);
    });
  }

  function updateCounter(){ if (counter) counter.textContent = (idx+1) + ' / ' + n; }

  function step(to){
    to = Math.min(Math.max(to, 0), n - 1);
    if (to === idx) return;
    finalize();               // collapse any prior in-flight step to its target first
    var from = idx, navDir = to > from ? 1 : -1;
    idx = to; updateCounter();
    if (!animated){ finalize(); return; }
    var toEl = slides[to], fromEl = slides[from];
    clearVars(fromEl); clearVars(toEl);
    fromEl.classList.remove('is-current','slide-entering','slide-leaving');
    toEl.classList.remove('is-current','slide-entering','slide-leaving');
    fromEl.classList.add('is-visible'); toEl.classList.add('is-visible');
    setVars(toEl, motionVars(navDir, 'enter'));
    setVars(fromEl, motionVars(navDir, 'leave'));
    void toEl.offsetWidth;    // reflow so the animations (re)start on repeat navigation
    toEl.classList.add('slide-entering');
    fromEl.classList.add('slide-leaving');
    leaveTimer = setTimeout(function(){ leaveTimer = null; finalize(); }, T.durationMs + 80);
  }
  function go(delta){ step(idx + delta); }

  function toggleFs(){
    if (document.fullscreenElement) document.exitFullscreen && document.exitFullscreen();
    else if (document.documentElement.requestFullscreen)
      document.documentElement.requestFullscreen().catch(function(){});
  }

  // Init: first slide current.
  slides[0].classList.add('is-visible','is-current');
  updateCounter();

  document.addEventListener('keydown', function(e){
    switch (e.key) {
      case 'ArrowRight': case 'PageDown': case ' ': e.preventDefault(); go(1); break;
      case 'ArrowLeft': case 'PageUp': e.preventDefault(); go(-1); break;
      case 'Home': e.preventDefault(); step(0); break;
      case 'End': e.preventDefault(); step(n-1); break;
      case 'f': case 'F': toggleFs(); break;
      case 'Escape': if (document.fullscreenElement) document.exitFullscreen(); break;
    }
  });

  // Click the stage to advance; clicks on the controls are handled by their own buttons.
  document.querySelector('.stage-wrap').addEventListener('click', function(e){
    if (e.target.closest('.controls')) return;
    go(1);
  });
  var prev = document.querySelector('[data-nav="prev"]');
  var next = document.querySelector('[data-nav="next"]');
  var fs = document.querySelector('[data-nav="fs"]');
  if (prev) prev.addEventListener('click', function(){ go(-1); });
  if (next) next.addEventListener('click', function(){ go(1); });
  if (fs) fs.addEventListener('click', toggleFs);
})();`;
}

// Build the complete standalone presentation HTML document. Pure — given the resolved
// snapshot and baked-image maps, it does no IO, so it's testable without a browser.
export function buildDeckHtml(opts: {
  title: string;
  // ACTIVE slides only. The page number is NOT the index any more: a cover draws none and
  // consumes none (§19.0.3), and an evidence slide is numbered under its parent (§18.2.9), so
  // it comes from the one numbering helper every other render path uses. The exported file is
  // deliberately LINEAR (§18.2.10/§18.3.7) — every active slide, in array order, which is
  // already storyline → its evidence → the backup section.
  slides: SlideData[];
  styleSheet: GriddStyleSheetData;
  transition: SlideTransition;
  snapshots: Record<string, GriddSnapshot>;
  images: Record<string, BakedImage>;
  // §21/§11.2 diagramDocId → the diagram's SVG string, fonts inlined. Absent entries render
  // an empty frame, exactly as a missing grid snapshot does.
  diagrams?: Record<string, string>;
  // §26/§9.2 ganttDocId → a PNG data URL, already baked. Absent entries render an empty
  // frame, exactly as a missing grid snapshot does.
  gantts?: Record<string, string>;
  // §15/§9.2 chartDocId → an SVG STRING, already serialized. Absent entries render an empty
  // frame, exactly as an absent grid snapshot does.
  charts?: Record<string, string>;
  // §24.4.1 The deck's logo, already baked to a data URL. Absent = no mark.
  logo?: BakedLogo | null;
}): string {
  const { title, slides, styleSheet, transition, snapshots, images } = opts;
  const diagrams = opts.diagrams ?? {};
  const gantts = opts.gantts ?? {};
  const charts = opts.charts ?? {};
  const logo = opts.logo ?? null;
  const docTitle = title.trim() || "Untitled deck";
  const numbers = slideNumbering(slides);
  const layers = slides
    .map(
      (s, i) =>
        `<div class="slide-motion" data-index="${i}">${renderSlideCard(
          s,
          numbers[i],
          styleSheet,
          snapshots,
          images,
          diagrams,
          gantts,
          charts,
          logo,
        )}</div>`,
    )
    .join("\n");

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(docTitle)}</title>
<style>${styleBlock()}</style>
</head>
<body>
<div class="stage-wrap">
  <div class="slide-stage">
${layers}
  </div>
  <div class="controls" role="toolbar" aria-label="Slide navigation">
    <button type="button" data-nav="prev" aria-label="Previous slide" title="Previous (←)">‹</button>
    <span class="counter" aria-live="polite">1 / ${slides.length}</span>
    <button type="button" data-nav="next" aria-label="Next slide" title="Next (→)">›</button>
    <button type="button" data-nav="fs" aria-label="Toggle fullscreen" title="Fullscreen (F)">⛶</button>
  </div>
</div>
<script>${playerScript(transition)}</script>
</body>
</html>`;
}

// ── Browser-side: bake image assets to self-contained data URLs ──────────────────────────

// Read a blob as a data: URL.
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

// Load an object URL into an <img> so we can read its natural size and draw it.
function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image decode failed"));
    img.src = url;
  });
}

// Flatten one raster image's crop + quarter-turn + flip + filters onto a canvas and return a
// data URL. This bakes ImageBlock's render pipeline at export time so the exported <img> is a
// plain, self-contained bitmap (no view-time layout math). SVG skips this (handled by caller).
async function bakeRaster(
  imgEl: HTMLImageElement,
  content: ImageContent,
): Promise<string> {
  const NW = imgEl.naturalWidth;
  const NH = imgEl.naturalHeight;
  const raw = content.crop ?? { x: 0, y: 0, width: NW, height: NH };
  // Clamp the crop to the image's real bounds (same guard as coverCrop).
  const cx = Math.max(0, raw.x);
  const cy = Math.max(0, raw.y);
  const cw = Math.min(NW, raw.x + raw.width) - cx;
  const ch = Math.min(NH, raw.y + raw.height) - cy;
  if (!(cw > 0) || !(ch > 0)) throw new Error("empty crop");

  const rotation = content.transform?.rotation ?? 0;
  const quarter = rotation === 90 || rotation === 270;
  const canvas = document.createElement("canvas");
  canvas.width = quarter ? ch : cw;
  canvas.height = quarter ? cw : ch;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no 2d context");

  const filter = cssFilter(content.filters);
  if (filter) ctx.filter = filter;
  ctx.translate(canvas.width / 2, canvas.height / 2);
  if (rotation) ctx.rotate((rotation * Math.PI) / 180);
  ctx.scale(
    content.transform?.flipH ? -1 : 1,
    content.transform?.flipV ? -1 : 1,
  );
  ctx.drawImage(imgEl, cx, cy, cw, ch, -cw / 2, -ch / 2, cw, ch);

  // Re-encode: keep PNG (may carry transparency) as PNG; everything else as JPEG to keep the
  // file small. The bitmap is already flattened, so no data is lost either way.
  return content.mime === "image/png"
    ? canvas.toDataURL("image/png")
    : canvas.toDataURL("image/jpeg", 0.92);
}

// Bake every image content across the given slides into a griddDocId-independent
// assetId→BakedImage map. Reads each blob from IndexedDB. A missing asset or a bake failure
// is skipped (that window renders empty in the export) rather than aborting the whole export.
export async function collectImageDataUrls(
  slides: SlideData[],
): Promise<Record<string, BakedImage>> {
  const contents: ImageContent[] = [];
  const seen = new Set<string>();
  for (const slide of slides) {
    for (const c of slide.contents) {
      if (c?.kind === "image" && !seen.has(c.assetId)) {
        seen.add(c.assetId);
        contents.push(c);
      }
    }
  }

  const out: Record<string, BakedImage> = {};
  await Promise.all(
    contents.map(async (content) => {
      try {
        const record = await getAsset(content.assetId);
        if (!record) return;
        if (content.mime === "image/svg+xml") {
          // Vector: embed as-is, no crop pipeline — matching ImageBlock, including
          // reading the fit through the shared helper rather than hardcoding it.
          out[content.assetId] = {
            dataUrl: await blobToDataUrl(record.blob),
            fit: effectiveFit(content),
          };
          return;
        }
        const objectUrl = URL.createObjectURL(record.blob);
        try {
          const imgEl = await loadImage(objectUrl);
          out[content.assetId] = {
            dataUrl: await bakeRaster(imgEl, content),
            fit: effectiveFit(content),
          };
        } finally {
          URL.revokeObjectURL(objectUrl);
        }
      } catch (err) {
        console.warn("[htmlExport] image bake failed", content.assetId, err);
      }
    }),
  );
  return out;
}

// §21/§11.2 Render every diagram on the given slides to an SVG string, fonts INLINED
// (§5.4) — the in-app cache deliberately skips them, and a file whose fonts live in the
// app's own bundle is not the fully-offline single file §6.6 promises.
//
// Runs off the STORE's diagram bodies rather than the render cache: the export must not
// depend on a window having been on screen. One unrenderable diagram costs that window, not
// the export.
export async function collectDiagramSvgs(
  slides: SlideData[],
  docs: Record<string, DiagramDocument>,
): Promise<Record<string, string>> {
  const ids = new Set<string>();
  for (const slide of slides) {
    for (const c of slide.contents) {
      if (c?.kind === "diagram") ids.add(c.diagramDocId);
    }
  }
  const out: Record<string, string> = {};
  await Promise.all(
    [...ids].map(async (docId) => {
      const doc = docs[docId];
      if (!doc) return;
      const svg = await renderDiagramSvgForExport(doc);
      if (svg) out[docId] = svg;
    }),
  );
  return out;
}

// §24.4.1 Bake the deck's logo for the export. `collectImageDataUrls` walks slide contents
// and the logo is in none of them, so it is one extra `getAsset` beside that pass — and one
// data URL, keeping the exported file the fully offline single file §6.6 promises.
export async function collectLogoDataUrl(
  logo: DeckLogo | undefined,
): Promise<BakedLogo | null> {
  if (!logo) return null;
  try {
    const record = await getAsset(logo.assetId);
    if (!record) return null;
    return {
      dataUrl: await blobToDataUrl(record.blob),
      corner: logo.corner,
      sizePct: logo.sizePct,
    };
  } catch (err) {
    console.warn("[htmlExport] logo bake failed", logo.assetId, err);
    return null;
  }
}

// Hand the exported HTML to the user as `<name>.html` — a download on the web, the OS save
// panel in the desktop shell (§14.7.4). Fire-and-forget: the caller has nothing to do with
// the outcome, and a cancelled save panel is not an error.
export function downloadHtml(html: string, fileName: string): void {
  void saveFile(html, fileName, "text/html;charset=utf-8");
}
